import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { catchError, of, switchMap, takeWhile, tap, timer } from 'rxjs';

import { AuthService } from '@tfs/auth';

import { AccountResetApi } from '../data-access/account-reset.api';
import { AccountResetProgress, isResetRunning } from '../models/account-reset.models';

export const ACCOUNT_RESET_POLL_INTERVAL_MS = 3000;

/** How many polls may find the reset not started before it is taken as stuck (about one minute). */
export const ACCOUNT_RESET_MAX_EMPTY_POLLS = 20;

/**
 * Blocks the app while the owner's account is being reset, so nothing is worked on in an account
 * whose data is being removed. Polls the reset until it has finished: on completion the app
 * restarts into onboarding; on failure the owner can resume the reset or sign out.
 */
@Component({
  selector: 'tfs-account-reset-progress-modal',
  imports: [MatButtonModule, MatDialogModule, MatProgressBarModule],
  templateUrl: './account-reset-progress-modal.component.html',
  styleUrl: './account-reset-progress-modal.component.scss',
  host: { '[class.without-actions]': '!failed()' },
})
export class AccountResetProgressModalComponent {
  /** One progress modal at a time, however often it is asked for. */
  static readonly dialogId = 'account-reset-progress';

  private readonly api = inject(AccountResetApi);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly progress = signal<AccountResetProgress | null>(null);
  protected readonly resuming = signal(false);
  protected readonly resumeFailed = signal(false);

  /** The reset was requested but has not started for too long, so the owner is not left waiting on it. */
  protected readonly stalled = signal(false);
  private emptyPolls = 0;

  protected readonly failed = computed(
    () => this.progress()?.status === 'Failed' || this.stalled()
  );

  /** The reset once the backend has started it; null while it is queued. */
  protected readonly started = computed(() => {
    const progress = this.progress();
    return progress && progress.status !== 'Queued' ? progress : null;
  });

  /** Modules done, plus the final reset of the account itself, out of all of them. */
  protected readonly percentDone = computed(() => {
    const progress = this.progress();
    if (!progress || progress.stepsTotal === 0) {
      return 0;
    }

    const done = progress.stepsCompleted + (progress.status === 'Completed' ? 1 : 0);
    return Math.round((done / (progress.stepsTotal + 1)) * 100);
  });

  /** When the progress was last polled, so the time running moves on even when nothing else changes. */
  private readonly polledAt = signal(Date.now());

  /** Whole seconds since the reset was requested. */
  protected readonly secondsRunning = computed(() => {
    const progress = this.started();
    return progress
      ? Math.floor((this.polledAt() - Date.parse(progress.requestedOnUtc)) / 1000)
      : 0;
  });

  /** How long the reset has been running, e.g. "45 seconds", "1 minute" or "6 minutes". */
  protected readonly runningFor = computed(() => {
    const seconds = this.secondsRunning();
    const [count, unit] = seconds < 60 ? [seconds, 'second'] : [Math.floor(seconds / 60), 'minute'];
    return `${count} ${unit}${count === 1 ? '' : 's'}`;
  });

  /** What is being removed right now, e.g. "tools and assemblies"; empty between stages. */
  protected readonly currentSteps = computed(() =>
    (this.progress()?.currentSteps ?? []).join(', ')
  );

  constructor() {
    this.poll();
  }

  protected resume(): void {
    this.resuming.set(true);
    this.resumeFailed.set(false);

    this.api.resetAccount().subscribe({
      next: () => {
        this.resuming.set(false);
        this.progress.set(null);
        this.stalled.set(false);
        this.emptyPolls = 0;
        this.poll();
      },
      error: () => {
        this.resuming.set(false);
        this.resumeFailed.set(true);
      },
    });
  }

  protected signOut(): void {
    this.authService.logout();
  }

  private poll(): void {
    timer(0, ACCOUNT_RESET_POLL_INTERVAL_MS)
      .pipe(
        // A failed read keeps the last known state; the next poll tries again
        switchMap(() => this.api.getProgress().pipe(catchError(() => of(undefined)))),
        tap(progress => {
          if (progress !== undefined) {
            this.progress.set(progress);
            this.polledAt.set(Date.now());
            this.emptyPolls = progress && progress.status !== 'Queued' ? 0 : this.emptyPolls + 1;
            this.stalled.set(this.emptyPolls >= ACCOUNT_RESET_MAX_EMPTY_POLLS);
          }
        }),
        takeWhile(progress => !this.stalled() && (!progress || isResetRunning(progress)), true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(progress => {
        if (progress?.status === 'Completed') {
          // Starts the app afresh, so every cached view is gone and onboarding opens for the empty account
          window.location.assign('/');
        }
      });
  }
}
